import type { ValidationChecklistItem } from 'components/ValidationChecklist/types';

import {
    STATUS_LABEL_ERROR,
    STATUS_LABEL_OK,
    STATUS_LABEL_WARN
} from 'pages/MixApplyPage/components/MixApplyCalculationsPanel/components/WeatherValidationCard/constants';
import type {
    WeatherValidationItem,
    WeatherValidationStatus
} from 'pages/MixApplyPage/components/MixApplyCalculationsPanel/components/WeatherValidationCard/types';
import {
    DICAMBA_FEDERAL_THRESHOLDS,
    DICAMBA_OVERLAY_TITLE_SUFFIX,
    INVERSION_LABEL,
    INVERSION_VALUE,
    ORG_DEFAULTS_CARD_TITLE,
    WEATHER_THRESHOLDS
} from 'pages/MixApplyPage/components/MixApplyCalculationsPanel/constants';
import type {
    ApplicationFixture,
    MixRecipe,
    StatusLevel,
    WeatherReadingFixture
} from 'pages/MixApplyPage/components/MixApplyCalculationsPanel/types';
import useTranslation from 'pages/MixApplyPage/hooks/useTranslation';

type UseDataParams = {
    application: ApplicationFixture;
    mixRecipe: MixRecipe | null;
    weather: WeatherReadingFixture;
};

export type WeatherValidationCardData = {
    items: WeatherValidationItem[];
    status: WeatherValidationStatus;
    statusLabel: string;
    title: string;
};

type UseDataReturn = {
    dicambaWarningVisible: boolean;
    stateComplianceItems: ValidationChecklistItem[];
    statusLevel: StatusLevel;
    statusText: string;
    weatherValidationCards: WeatherValidationCardData[];
};

const buildOrgDefaultsCard = (
    weather: WeatherReadingFixture,
    translation: ReturnType<typeof useTranslation>
): WeatherValidationCardData => {
    const { GUST, HUMIDITY, TEMPERATURE, WIND_SPEED } = translation;

    const windOk = weather.windSpeedMph <= WEATHER_THRESHOLDS.WIND_MAX_MPH;
    const gustOk = weather.gustMph <= WEATHER_THRESHOLDS.GUST_MAX_MPH;
    const tempOk =
        weather.temperatureF >= WEATHER_THRESHOLDS.TEMP_MIN_F &&
        weather.temperatureF <= WEATHER_THRESHOLDS.TEMP_MAX_F;
    const humidityOk = weather.humidityPct >= WEATHER_THRESHOLDS.HUMIDITY_MIN_PCT;

    const items: WeatherValidationItem[] = [
        {
            label: `${WIND_SPEED} (max ${WEATHER_THRESHOLDS.WIND_MAX_MPH} mph)`,
            pass: windOk,
            value: `${weather.windSpeedMph} mph`
        },
        {
            label: `${GUST} (max ${WEATHER_THRESHOLDS.GUST_MAX_MPH} mph)`,
            pass: gustOk,
            value: `${weather.gustMph} mph`
        },
        {
            label: `${TEMPERATURE} (max ${WEATHER_THRESHOLDS.TEMP_MAX_F})`,
            pass: tempOk,
            value: `${weather.temperatureF}`
        },
        {
            label: `${HUMIDITY} (min ${WEATHER_THRESHOLDS.HUMIDITY_MIN_PCT}%)`,
            pass: humidityOk,
            value: `${weather.humidityPct}%`
        }
    ];

    const allPass = items.every(item => item.pass === true);
    const status: WeatherValidationStatus = allPass ? 'ok' : 'error';
    const statusLabel = allPass ? STATUS_LABEL_OK : STATUS_LABEL_ERROR;

    return { items, status, statusLabel, title: ORG_DEFAULTS_CARD_TITLE };
};

const buildDicambaOverlayCard = (
    weather: WeatherReadingFixture,
    dicambaProductNames: string[]
): WeatherValidationCardData => {
    const windOk =
        weather.windSpeedMph >= DICAMBA_FEDERAL_THRESHOLDS.WIND_MIN_MPH &&
        weather.windSpeedMph <= DICAMBA_FEDERAL_THRESHOLDS.WIND_MAX_MPH;
    const tempOk = weather.temperatureF <= DICAMBA_FEDERAL_THRESHOLDS.TEMP_MAX_F;

    const items: WeatherValidationItem[] = [
        {
            label: `Wind ${DICAMBA_FEDERAL_THRESHOLDS.WIND_MIN_MPH}-${DICAMBA_FEDERAL_THRESHOLDS.WIND_MAX_MPH} mph (federal)`,
            pass: windOk,
            value: `${weather.windSpeedMph} mph`
        },
        {
            label: `Temp <= ${DICAMBA_FEDERAL_THRESHOLDS.TEMP_MAX_F} F (federal)`,
            pass: tempOk,
            value: `${weather.temperatureF}`
        },
        { label: INVERSION_LABEL, value: INVERSION_VALUE }
    ];

    const conditionsOk = windOk && tempOk;
    const status: WeatherValidationStatus = conditionsOk ? 'warn' : 'error';
    const statusLabel = conditionsOk ? STATUS_LABEL_WARN : STATUS_LABEL_ERROR;
    const title = `${dicambaProductNames.join(', ')} ${DICAMBA_OVERLAY_TITLE_SUFFIX}`;

    return { items, status, statusLabel, title };
};

const buildWeatherCards = (
    weather: WeatherReadingFixture,
    mixRecipe: MixRecipe | null,
    translation: ReturnType<typeof useTranslation>
): WeatherValidationCardData[] => {
    const cards = [buildOrgDefaultsCard(weather, translation)];

    const dicambaProductNames = (mixRecipe?.products ?? [])
        .filter(product => product.isDicamba === true)
        .map(product => product.productName);

    if (dicambaProductNames.length > 0) {
        cards.push(buildDicambaOverlayCard(weather, dicambaProductNames));
    }

    return cards;
};

const buildStateItems = (
    application: ApplicationFixture,
    mixRecipe: MixRecipe | null,
    translation: ReturnType<typeof useTranslation>
): ValidationChecklistItem[] => {
    const { LICENSE_VALID, licenseDeadline, productLicenseBlock } = translation;
    const { state } = application;
    const products = mixRecipe?.products ?? [];

    const licensingBlocks: ValidationChecklistItem[] = products.flatMap(product =>
        (product.unlicensedStates ?? []).includes(state)
            ? [{ level: 'block', text: productLicenseBlock(product.productName, state) }]
            : []
    );

    const dueDiligenceWarns: ValidationChecklistItem[] = products.flatMap(product =>
        (product.dueDiligences ?? [])
            .filter(diligence => diligence.state === state)
            .map(
                (diligence): ValidationChecklistItem => ({
                    level: 'warn',
                    text: `${product.productName}: ${diligence.requirement}`
                })
            )
    );

    return [
        ...licensingBlocks,
        ...dueDiligenceWarns,
        { level: 'ok', text: LICENSE_VALID },
        { level: 'warn', text: licenseDeadline(state) }
    ];
};

const useData = ({ application, mixRecipe, weather }: UseDataParams): UseDataReturn => {
    const translation = useTranslation();
    const { EMPTY_RECIPE, READY, WEATHER_ISSUE } = translation;

    const weatherValidationCards = buildWeatherCards(weather, mixRecipe, translation);
    const stateComplianceItems = buildStateItems(application, mixRecipe, translation);

    const hasRecipe = mixRecipe !== null && mixRecipe.products.length > 0;
    const weatherHasIssue = weatherValidationCards.some(card => card.status !== 'ok');

    let statusLevel: StatusLevel = 'ok';
    let statusText = READY;

    if (!hasRecipe) {
        statusLevel = 'warn';
        statusText = EMPTY_RECIPE;
    } else if (weatherHasIssue) {
        statusLevel = 'warn';
        statusText = WEATHER_ISSUE;
    }

    const dicambaWarningVisible =
        mixRecipe?.products.some(product => product.isDicamba === true) ?? false;

    return {
        dicambaWarningVisible,
        stateComplianceItems,
        statusLevel,
        statusText,
        weatherValidationCards
    };
};

export default useData;
